import Select from 'react-select'
import { useField, useFormikContext } from 'formik'
import './FormikSelect.scoped.scss'

const styles = {
  multiValue: (base, state) => state.data.isFixed ? { ...base, backgroundColor: 'gray' } : base,
  multiValueLabel: (base, state) => state.data.isFixed ? { ...base, fontWeight: 'bold', color: 'white', paddingRight: 6 } : base,
  multiValueRemove: (base, state) => state.data.isFixed ? { ...base, display: 'none' } : base,
}

function toErrorText(err: unknown): string {
  if (!err) return ''
  if (Array.isArray(err)) return err.filter(Boolean).join(', ')
  if (typeof err === 'object') {
    try {
      const vals = Object.values(err as Record<string, unknown>)
      const flat = (vals as any).flat ? (vals as any).flat() : vals
      return flat.filter(Boolean).map(String).join(', ')
    } catch {
      return String(err)
    }
  }
  return String(err)
}

// Wraps react-select for Formik.
// Supports both single and multi (via props.isMulti).
const FormikSelect = ({ name, options, ...props }) => {
  const [field, meta, helpers] = useField(name)
  const { submitCount } = useFormikContext()

  const optionWithValue = (v: any) => options?.find?.((o) => o.value === v)

  const getValue = () => {
    if (props.isMulti) {
      if (!Array.isArray(field.value)) return []
      return field.value.map(optionWithValue).filter(Boolean)
    }
    return optionWithValue(field.value) ?? null
  }

  const handleChange = (option: any, actionMeta: any) => {
    switch (actionMeta?.action) {
      case 'remove-value':
      case 'pop-value':
        if (actionMeta?.removedValue?.isFixed) return
        break
      default:
        break
    }

    const value = props.isMulti
      ? (Array.isArray(option) ? option.map((o) => o.value) : [])
      : option?.value ?? null

    helpers.setValue(value)
  }

  const errorText = (meta.touched || submitCount > 0) ? toErrorText(meta.error) : ''

  return (
    <div className={`formik-select ${errorText ? 'has-error' : ''}`}>
      <Select
        name={field.name}
        value={getValue()}
        options={options}
        styles={styles}
        onChange={handleChange}
        onBlur={() => helpers.setTouched(true)}
        isOptionDisabled={(o) => o.disabled}
        {...props}
      />
      {errorText && <div className="error-message margin-top margin-bottom">{errorText}</div>}
    </div>
  )
}

export default FormikSelect
